<template>
  <div class="flex flex-col h-full">
    <!-- Messages Area -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4">
      <ChatMessages :messages="messages" :is-streaming="isStreaming" :is-loading="isLoading" />

      <!-- Typing Indicator -->
      <div
        v-if="isTyping && !isStreaming && !isLoading"
        class="flex items-center space-x-2 text-sm text-muted-foreground mt-2"
      >
        <Icon name="mdi:loading" class="h-4 w-4 animate-spin" />
        <span>grep is thinking...</span>
      </div>

      <!-- Follow-up Questions -->
      <div v-if="followUpQuestions?.length && !isStreaming" class="mt-2 space-y-2">
        <p class="text-sm font-medium text-muted-foreground">Follow-up Questions:</p>
        <div class="flex flex-col gap-2">
          <Button
            v-for="question in followUpQuestions"
            :key="question"
            variant="outline"
            size="sm"
            class="text-left w-full justify-start whitespace-normal break-words min-h-[4rem] !p-0"
            @click="handleFollowUpQuestion(question)"
          >
            <span class="line-clamp-2 px-4 py-3">{{ question }}</span>
          </Button>
        </div>
      </div>
    </div>

    <!-- Chat Input -->
    <div class="border-t p-4">
      <ChatInput
        v-model="pendingMessage"
        :disabled="isLoading || isStreaming || disabled"
        @send="handleSend"
        @clear="handleClear"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onUnmounted, onMounted, computed } from 'vue'
import type { ChatType } from '@/types/chatEnums'
import type { ChatMessage } from '@/types/chat'
import ChatMessages from './ChatMessages.vue'
import ChatInput from './ChatInput.vue'

const props = defineProps<{
  chatType: ChatType
  identifier: string
  title?: string
  isLoading?: boolean
  isTyping?: boolean
  isStreaming?: boolean
  messages?: ChatMessage[]
  followUpQuestions?: string[]
  disabled?: boolean
}>()

const emit = defineEmits<{
  send: [content: string]
  clear: []
  'follow-up': [question: string]
}>()

// Initialize refs
const pendingMessage = ref('')
const shouldAutoScroll = ref(true)
const messagesContainer = ref<HTMLElement | null>(null)

// Computed properties with defaults
const messages = computed(() => props.messages || [])

// Modified scroll function with force option
const scrollToBottom = (force = false) => {
  // Wait for next tick and then a small delay to ensure messages are rendered
  nextTick(async () => {
    if (!messagesContainer.value) return

    // Add a small delay to ensure messages are rendered
    await new Promise((resolve) => setTimeout(resolve, 100))

    if (force || shouldAutoScroll.value) {
      log('🎯 Scrolling to bottom:', { force, shouldAutoScroll: shouldAutoScroll.value })
      const { scrollHeight } = messagesContainer.value
      messagesContainer.value.scrollTo({
        top: scrollHeight,
        behavior: force ? 'auto' : 'smooth',
      })
    }
  })
}

// Watch for streaming updates
watch(
  () => props.isStreaming,
  (newValue) => {
    log('🎯 ChatInterface streaming status:', {
      newValue,
      shouldAutoScroll: shouldAutoScroll.value,
    })
    if (newValue) {
      // When streaming starts, force scroll to bottom
      scrollToBottom(true)
    }
  }
)

// Add watcher for message content updates
watch(
  () => messages.value.map((m) => m.content),
  () => {
    log('🎯 Message content updated, shouldAutoScroll:', shouldAutoScroll.value)
    if (shouldAutoScroll.value) {
      scrollToBottom(false)
    }
  },
  { deep: true }
)

// Watch for new messages and scroll to bottom
watch(
  messages,
  async (newMessages, oldMessages) => {
    // Ensure messages is an array
    const messageArray = Array.isArray(newMessages) ? newMessages : []
    const oldArray = Array.isArray(oldMessages) ? oldMessages : []

    log('🎯 ChatInterface messages updated:', {
      oldCount: oldArray.length,
      newCount: messageArray.length,
      isNewMessage: messageArray.length > oldArray.length,
      shouldAutoScroll: shouldAutoScroll.value,
      isInitialLoad: oldArray.length === 0 && messageArray.length > 0,
    })

    // Force scroll on initial load or new message
    const isInitialLoad = oldArray.length === 0 && messageArray.length > 0
    const isNewMessage = messageArray.length > oldArray.length && oldArray.length > 0

    if (isInitialLoad) {
      log('🎯 Initial history load, forcing scroll to bottom')
      // Wait for messages to be rendered
      await nextTick()
      await new Promise((resolve) => setTimeout(resolve, 100))
      scrollToBottom(true)
    } else if (isNewMessage) {
      scrollToBottom(false)
    }
  },
  { deep: true, immediate: true }
)

// Add watcher for loading state
watch(
  () => props.isLoading,
  async (newValue, oldValue) => {
    if (oldValue && !newValue) {
      // Loading just finished
      log('🎯 Loading finished, scrolling to bottom')
      await nextTick()
      await new Promise((resolve) => setTimeout(resolve, 100))
      scrollToBottom(true)
    }
  }
)

// Handle scroll events
const handleScroll = () => {
  if (!messagesContainer.value) return
  const { scrollTop, scrollHeight, clientHeight } = messagesContainer.value
  const distanceFromBottom = scrollHeight - clientHeight - scrollTop
  const wasAtBottom = shouldAutoScroll.value
  shouldAutoScroll.value = distanceFromBottom < 100

  log('🎯 Scroll event:', {
    distanceFromBottom,
    wasAtBottom,
    isNowAtBottom: shouldAutoScroll.value,
    scrollHeight,
    clientHeight,
    scrollTop,
  })
}

// Add scroll listener when mounted
onMounted(async () => {
  log('🎯 ChatInterface mounted with messages:', messages.value)
  if (messagesContainer.value) {
    messagesContainer.value.addEventListener('scroll', handleScroll)
    // Wait for messages to be rendered
    await nextTick()
    await new Promise((resolve) => setTimeout(resolve, 100))
    scrollToBottom(true)
  }
})

// Handle sending messages
const handleSend = (content: string) => {
  log('🎯 ChatInterface handleSend:', {
    content,
    chatType: props.chatType as ChatType,
    identifier: props.identifier,
  })
  // Force scroll to bottom when sending new message
  shouldAutoScroll.value = true
  emit('send', content)
  pendingMessage.value = ''
}

// Handle follow-up questions
const handleFollowUpQuestion = (question: string) => {
  log('🎯 ChatInterface handleFollowUp:', {
    question,
    chatType: props.chatType as ChatType,
    identifier: props.identifier,
  })
  emit('follow-up', question)
}

// Handle clear history
const handleClear = () => {
  log('🎯 ChatInterface handleClear')
  emit('clear')
}

// Clean up scroll listener on unmount
onUnmounted(() => {
  if (messagesContainer.value) {
    messagesContainer.value.removeEventListener('scroll', handleScroll)
  }
})
</script>
